<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';

import ReportHeader from './components/ReportHeader.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import Spinner from 'shared/components/Spinner.vue';
import ContactAttributesReportsAPI from 'dashboard/api/contactAttributesReports';

const { t } = useI18n();

const isLoading = ref(false);
const reportData = ref(null);
const dateAttributeReport = ref(null);
const attributes = ref([]);
const dateAttributes = ref([]);
const selectedAttribute = ref(null);
const selectedAttributeValue = ref(null);
const selectedDateAttribute = ref(null);
const dateFilterType = ref('created'); // 'created' or 'attribute'
const groupBy = ref({ id: 'day', name: '' });

const currentDateRangeSelection = ref({
  id: 0,
  name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
});
const customDateRange = ref([new Date(), new Date()]);

const CUSTOM_DATE_RANGE_ID = 5;

const dateRange = computed(() => [
  { id: 0, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
  { id: 1, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
  { id: 2, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS') },
  { id: 3, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS') },
  { id: 4, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_YEAR') },
  { id: 5, name: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
]);

const groupByOptions = computed(() => [
  { id: 'day', name: t('REPORT.GROUPING_OPTIONS.DAY') },
  { id: 'week', name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  { id: 'month', name: t('REPORT.GROUPING_OPTIONS.MONTH') },
]);

const dateFilterOptions = computed(() => [
  { id: 'created', name: t('CONTACT_ATTRIBUTES_REPORTS.FILTER_BY_CREATED') },
  {
    id: 'attribute',
    name: t('CONTACT_ATTRIBUTES_REPORTS.FILTER_BY_ATTRIBUTE'),
  },
]);

// Options for list-type attributes
const attributeValueOptions = computed(() => {
  if (
    !selectedAttribute.value ||
    selectedAttribute.value.attribute_display_type !== 'list'
  ) {
    return [];
  }
  const values = selectedAttribute.value.attribute_values || [];
  return [
    { id: null, value: t('CONTACT_ATTRIBUTES_REPORTS.ALL_VALUES') },
    ...values.map(v => ({ id: v, value: v })),
  ];
});

const isListAttribute = computed(() => {
  return selectedAttribute.value?.attribute_display_type === 'list';
});

const isDateRangeSelected = computed(
  () => currentDateRangeSelection.value.id === CUSTOM_DATE_RANGE_ID
);

const fromDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(startOfDay(customDateRange.value[0]));
  }
  const dateRangeMap = { 0: 6, 1: 29, 2: 89, 3: 179, 4: 364 };
  const diff = dateRangeMap[currentDateRangeSelection.value.id];
  return getUnixTime(startOfDay(subDays(new Date(), diff)));
});

const toDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(endOfDay(customDateRange.value[1]));
  }
  return getUnixTime(endOfDay(new Date()));
});

const summary = computed(() => reportData.value?.summary || {});

const attributesData = computed(() => reportData.value?.attributes || []);

const chartData = computed(() => {
  // If filtering by date attribute, show that data
  if (
    dateFilterType.value === 'attribute' &&
    reportData.value?.by_attribute_date
  ) {
    const entries = Object.entries(reportData.value.by_attribute_date);
    return entries
      .map(([date, count]) => ({
        date: formatDate(date),
        rawDate: date,
        count,
      }))
      .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
  }

  // Default: show by created_at
  if (!reportData.value?.by_date) return [];

  const entries = Object.entries(reportData.value.by_date);
  return entries
    .map(([date, count]) => ({
      date: formatDate(date),
      rawDate: date,
      count,
    }))
    .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
});

const maxCount = computed(() => {
  if (!chartData.value.length) return 0;
  return Math.max(...chartData.value.map(d => d.count));
});

// Get date attribute report data
const dateAttrChartData = computed(() => {
  if (!dateAttributeReport.value?.by_date) return [];

  const entries = Object.entries(dateAttributeReport.value.by_date);
  return entries
    .map(([date, count]) => ({
      date: formatDate(date),
      rawDate: date,
      count,
    }))
    .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
});

const dateAttrMaxCount = computed(() => {
  if (!dateAttrChartData.value.length) return 0;
  return Math.max(...dateAttrChartData.value.map(d => d.count));
});

const dateAttrContacts = computed(
  () => dateAttributeReport.value?.contacts || []
);

// Contacts list for selected attribute
const attributeContacts = computed(() => reportData.value?.contacts || []);

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

function formatAttributeValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  // Check if it's a date string
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return formatDate(value);
  }
  return String(value);
}

function getBarHeight(count, max) {
  if (!max) return 0;
  return (count / max) * 100;
}

function getAttributeTypeLabel(type) {
  const types = {
    text: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.TEXT'),
    number: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.NUMBER'),
    currency: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.CURRENCY'),
    percent: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.PERCENT'),
    link: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.LINK'),
    date: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.DATE'),
    list: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.LIST'),
    checkbox: t('CONTACT_ATTRIBUTES_REPORTS.TYPES.CHECKBOX'),
  };
  return types[type] || type;
}

function isNumericAttribute(attr) {
  return ['number', 'currency', 'percent'].includes(
    attr.attribute_display_type
  );
}

function isDateAttribute(attr) {
  return attr.attribute_display_type === 'date';
}

function getMaxDistributionCount(distribution) {
  if (!Array.isArray(distribution)) return 0;
  return Math.max(...distribution.map(d => d.count), 1);
}

async function fetchAttributes() {
  try {
    const response = await ContactAttributesReportsAPI.getAttributes();
    const allAttributes = response.data;

    attributes.value = [
      {
        id: null,
        attribute_display_name: t('CONTACT_ATTRIBUTES_REPORTS.ALL_ATTRIBUTES'),
      },
      ...allAttributes,
    ];

    // Filter only date attributes
    dateAttributes.value = allAttributes.filter(
      a => a.attribute_display_type === 'date'
    );
  } catch (error) {
    console.error('Error fetching attributes:', error);
  }
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const params = {
      from: fromDate.value,
      to: toDate.value,
      attributeKey: selectedAttribute.value?.attribute_key,
      attributeValue: selectedAttributeValue.value?.id,
      groupBy: groupBy.value.id,
    };

    // If filtering by date attribute, add the date attribute params
    if (dateFilterType.value === 'attribute' && selectedDateAttribute.value) {
      params.dateAttributeKey = selectedDateAttribute.value.attribute_key;
      params.dateFilterType = 'attribute';
    }

    const response = await ContactAttributesReportsAPI.getReport(params);
    reportData.value = response.data;
  } catch (error) {
    console.error('Error fetching contact attributes report:', error);
  } finally {
    isLoading.value = false;
  }
}

async function fetchDateAttributeReport() {
  if (!selectedDateAttribute.value) {
    dateAttributeReport.value = null;
    return;
  }

  try {
    const response = await ContactAttributesReportsAPI.getByDateAttribute({
      from: fromDate.value,
      to: toDate.value,
      attributeKey: selectedDateAttribute.value.attribute_key,
      groupBy: groupBy.value.id,
    });
    dateAttributeReport.value = response.data;
  } catch (error) {
    console.error('Error fetching date attribute report:', error);
  }
}

function onDateRangeChange(selectedRange) {
  currentDateRangeSelection.value = selectedRange;
  fetchReport();
  if (selectedDateAttribute.value) {
    fetchDateAttributeReport();
  }
}

function onCustomDateChange(value) {
  customDateRange.value = value;
  fetchReport();
  if (selectedDateAttribute.value) {
    fetchDateAttributeReport();
  }
}

function onAttributeChange() {
  // Reset value filter when attribute changes
  selectedAttributeValue.value = null;
  fetchReport();
}

function onAttributeValueChange() {
  fetchReport();
}

function onDateAttributeChange() {
  fetchReport();
  fetchDateAttributeReport();
}

function onDateFilterTypeChange() {
  fetchReport();
}

function onGroupByChange() {
  fetchReport();
  if (selectedDateAttribute.value) {
    fetchDateAttributeReport();
  }
}

// Watch for date filter type changes
watch(dateFilterType, () => {
  if (
    dateFilterType.value === 'attribute' &&
    !selectedDateAttribute.value &&
    dateAttributes.value.length > 0
  ) {
    selectedDateAttribute.value = dateAttributes.value[0];
  }
});

onMounted(async () => {
  // Initialize groupBy with the first option
  groupBy.value = groupByOptions.value[0];
  await fetchAttributes();
  fetchReport();
});
</script>

<template>
  <div>
    <ReportHeader :header-title="$t('CONTACT_ATTRIBUTES_REPORTS.HEADER')" />

    <!-- Filters -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
      <!-- Attribute Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.FILTER_ATTRIBUTE') }}
        </p>
        <multiselect
          v-model="selectedAttribute"
          track-by="id"
          label="attribute_display_name"
          :placeholder="$t('CONTACT_ATTRIBUTES_REPORTS.SELECT_ATTRIBUTE')"
          :options="attributes"
          :searchable="true"
          :allow-empty="true"
          :show-labels="false"
          @update:model-value="onAttributeChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{ option.attribute_display_name }}</span>
          </template>
          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <span>{{ option.attribute_display_name }}</span>
              <span
                v-if="option.attribute_display_type"
                class="text-xs text-n-slate-10"
              >
                ({{ getAttributeTypeLabel(option.attribute_display_type) }})
              </span>
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Date Attribute Filter (for date-type attributes) -->
      <div v-if="dateAttributes.length > 0" class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.DATE_ATTRIBUTE') }}
        </p>
        <multiselect
          v-model="selectedDateAttribute"
          track-by="id"
          label="attribute_display_name"
          :placeholder="$t('CONTACT_ATTRIBUTES_REPORTS.SELECT_DATE_ATTRIBUTE')"
          :options="dateAttributes"
          :searchable="true"
          :allow-empty="true"
          :show-labels="false"
          @update:model-value="onDateAttributeChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{ option.attribute_display_name }}</span>
          </template>
        </multiselect>
      </div>

      <!-- Value Filter (for list-type attributes) -->
      <div v-if="isListAttribute" class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.FILTER_VALUE') }}
        </p>
        <multiselect
          v-model="selectedAttributeValue"
          track-by="id"
          label="value"
          :placeholder="$t('CONTACT_ATTRIBUTES_REPORTS.SELECT_VALUE')"
          :options="attributeValueOptions"
          :searchable="true"
          :allow-empty="true"
          :show-labels="false"
          @update:model-value="onAttributeValueChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{ option.value }}</span>
          </template>
        </multiselect>
      </div>

      <!-- Date Filter Type (when date attribute selected) -->
      <div v-if="selectedDateAttribute" class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.DATE_FILTER_TYPE') }}
        </p>
        <multiselect
          v-model="dateFilterType"
          track-by="id"
          label="name"
          :options="dateFilterOptions"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
          @update:model-value="onDateFilterTypeChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{
              option.name || $t('CONTACT_ATTRIBUTES_REPORTS.FILTER_BY_CREATED')
            }}</span>
          </template>
        </multiselect>
      </div>

      <!-- Date Range Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('REPORT.DURATION_FILTER_LABEL') }}
        </p>
        <multiselect
          v-model="currentDateRangeSelection"
          track-by="id"
          label="name"
          :options="dateRange"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
          @select="onDateRangeChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{ option.name }}</span>
          </template>
        </multiselect>
      </div>

      <!-- Group By Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL') }}
        </p>
        <multiselect
          v-model="groupBy"
          track-by="id"
          label="name"
          :options="groupByOptions"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
          @update:model-value="onGroupByChange"
        >
          <template #singleLabel="{ option }">
            <span class="text-sm">{{ option.name }}</span>
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Custom Date Range -->
    <div v-if="isDateRangeSelected" class="mb-6">
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
      </p>
      <WootDateRangePicker
        show-range
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateChange"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Report Content -->
    <div v-else-if="reportData">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.TOTAL_CONTACTS') }}
          </p>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ summary.total_contacts || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.WITH_ATTRIBUTES') }}
          </p>
          <p class="text-2xl font-semibold text-n-teal-11">
            {{ summary.contacts_with_attributes || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.WITHOUT_ATTRIBUTES') }}
          </p>
          <p class="text-2xl font-semibold text-n-amber-11">
            {{ summary.contacts_without_attributes || 0 }}
          </p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <p class="text-sm text-n-slate-11 mb-1">
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.FILL_RATE') }}
          </p>
          <p class="text-2xl font-semibold text-n-blue-11">
            {{ summary.fill_rate || 0 }}%
          </p>
        </div>
      </div>

      <!-- Contacts List for Selected Attribute -->
      <div
        v-if="
          selectedAttribute && selectedAttribute.id && attributeContacts.length
        "
        class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.CONTACTS_LIST') }} -
          {{ selectedAttribute.attribute_display_name }}
          <span class="text-sm font-normal text-n-slate-10 ml-2">({{ attributeContacts.length }}
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.CONTACTS_FOUND') }})</span>
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.NAME') }}
                </th>
                <th
                  class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.EMAIL') }}
                </th>
                <th
                  class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.PHONE') }}
                </th>
                <th
                  class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                >
                  {{ selectedAttribute.attribute_display_name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contact in attributeContacts"
                :key="contact.id"
                class="border-b border-n-weak hover:bg-n-alpha-2"
              >
                <td class="py-2 px-3 text-sm text-n-slate-12">
                  {{ contact.name || '-' }}
                </td>
                <td class="py-2 px-3 text-sm text-n-slate-11">
                  {{ contact.email || '-' }}
                </td>
                <td class="py-2 px-3 text-sm text-n-slate-11">
                  {{ contact.phone_number || '-' }}
                </td>
                <td class="py-2 px-3 text-sm text-n-teal-11 font-medium">
                  {{ formatAttributeValue(contact.attribute_value) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Chart - Contacts by Date -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{
            dateFilterType === 'attribute' && selectedDateAttribute
              ? $t('CONTACT_ATTRIBUTES_REPORTS.CHART_BY_ATTRIBUTE_DATE', {
                  attribute: selectedDateAttribute.attribute_display_name,
                })
              : $t('CONTACT_ATTRIBUTES_REPORTS.CHART_TITLE')
          }}
        </h3>
        <div v-if="chartData.length" class="h-64">
          <div class="flex items-end justify-between h-full gap-1">
            <div
              v-for="item in chartData"
              :key="item.rawDate"
              class="flex flex-col items-center flex-1"
            >
              <span class="text-xs text-n-slate-11 mb-1">{{ item.count }}</span>
              <div
                class="w-full bg-n-teal-9 rounded-t transition-all duration-300"
                :style="{
                  height: `${getBarHeight(item.count, maxCount)}%`,
                  minHeight: item.count > 0 ? '4px' : '0',
                }"
              />
              <span
                class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
              >
                {{ item.date }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-11"
        >
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.NO_DATA') }}
        </div>
      </div>

      <!-- Date Attribute Report Section (when a date attribute is selected) -->
      <div
        v-if="dateAttributeReport && selectedDateAttribute"
        class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{
            $t('CONTACT_ATTRIBUTES_REPORTS.DATE_ATTRIBUTE_REPORT', {
              attribute: selectedDateAttribute.attribute_display_name,
            })
          }}
        </h3>

        <!-- Date attribute summary -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="bg-n-alpha-3 rounded-lg p-3">
            <p class="text-sm text-n-slate-11">
              {{ $t('CONTACT_ATTRIBUTES_REPORTS.TOTAL_WITH_DATE') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ dateAttributeReport.summary?.total_contacts || 0 }}
            </p>
          </div>
          <div class="bg-n-alpha-3 rounded-lg p-3">
            <p class="text-sm text-n-slate-11">
              {{ $t('CONTACT_ATTRIBUTES_REPORTS.UNIQUE_DATES') }}
            </p>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ dateAttributeReport.summary?.unique_dates || 0 }}
            </p>
          </div>
        </div>

        <!-- Date attribute chart -->
        <div v-if="dateAttrChartData.length" class="h-48 mb-4">
          <div class="flex items-end justify-between h-full gap-1">
            <div
              v-for="item in dateAttrChartData"
              :key="item.rawDate"
              class="flex flex-col items-center flex-1"
            >
              <span class="text-xs text-n-slate-11 mb-1">{{ item.count }}</span>
              <div
                class="w-full bg-n-violet-9 rounded-t transition-all duration-300"
                :style="{
                  height: `${getBarHeight(item.count, dateAttrMaxCount)}%`,
                  minHeight: item.count > 0 ? '4px' : '0',
                }"
              />
              <span
                class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
              >
                {{ item.date }}
              </span>
            </div>
          </div>
        </div>

        <!-- Contacts list -->
        <div v-if="dateAttrContacts.length" class="mt-4">
          <h4 class="text-sm font-medium text-n-slate-11 mb-2">
            {{ $t('CONTACT_ATTRIBUTES_REPORTS.CONTACTS_LIST') }}
          </h4>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                  >
                    {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.NAME') }}
                  </th>
                  <th
                    class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                  >
                    {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.EMAIL') }}
                  </th>
                  <th
                    class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                  >
                    {{ $t('CONTACT_ATTRIBUTES_REPORTS.TABLE.PHONE') }}
                  </th>
                  <th
                    class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                  >
                    {{ selectedDateAttribute.attribute_display_name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="contact in dateAttrContacts"
                  :key="contact.id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-2 px-3 text-sm text-n-slate-12">
                    {{ contact.name || '-' }}
                  </td>
                  <td class="py-2 px-3 text-sm text-n-slate-11">
                    {{ contact.email || '-' }}
                  </td>
                  <td class="py-2 px-3 text-sm text-n-slate-11">
                    {{ contact.phone_number || '-' }}
                  </td>
                  <td class="py-2 px-3 text-sm text-n-violet-11 font-medium">
                    {{ formatDate(contact.attribute_date) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Attributes Breakdown -->
      <div class="space-y-4">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.ATTRIBUTES_BREAKDOWN') }}
        </h3>

        <div
          v-for="attr in attributesData"
          :key="attr.id"
          class="bg-n-solid-2 rounded-lg p-4 border border-n-weak"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <h4 class="font-medium text-n-slate-12">
                {{ attr.attribute_display_name }}
              </h4>
              <span
                v-if="isDateAttribute(attr)"
                class="px-2 py-0.5 text-xs bg-n-violet-3 text-n-violet-11 rounded-full"
              >
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.TYPES.DATE') }}
              </span>
            </div>
            <div class="text-right">
              <p class="text-lg font-semibold text-n-slate-12">
                {{ attr.stats.fill_rate }}%
              </p>
              <p class="text-xs text-n-slate-10">
                {{ attr.stats.filled_count }}/{{
                  attr.stats.filled_count + attr.stats.empty_count
                }}
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.FILLED') }}
              </p>
            </div>
          </div>

          <p class="text-xs text-n-slate-10 mb-3">
            {{ attr.attribute_key }} -
            {{ getAttributeTypeLabel(attr.attribute_display_type) }}
          </p>

          <!-- Progress bar for fill rate -->
          <div class="w-full h-2 bg-n-weak rounded-full mb-4">
            <div
              class="h-full bg-n-teal-9 rounded-full transition-all"
              :style="{ width: `${attr.stats.fill_rate}%` }"
            />
          </div>

          <!-- Value Distribution -->
          <div v-if="Array.isArray(attr.value_distribution)" class="space-y-2">
            <p class="text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('CONTACT_ATTRIBUTES_REPORTS.VALUE_DISTRIBUTION') }}
            </p>
            <div
              v-for="item in attr.value_distribution"
              :key="item.value"
              class="flex items-center gap-3"
            >
              <span
                class="text-sm text-n-slate-12 min-w-32 truncate"
                :title="item.value"
              >
                {{ item.value || $t('CONTACT_ATTRIBUTES_REPORTS.EMPTY_VALUE') }}
              </span>
              <div class="flex-1 h-4 bg-n-weak rounded overflow-hidden">
                <div
                  class="h-full bg-n-blue-9 rounded transition-all"
                  :style="{
                    width: `${(item.count / getMaxDistributionCount(attr.value_distribution)) * 100}%`,
                  }"
                />
              </div>
              <span class="text-sm text-n-slate-11 min-w-12 text-right">
                {{ item.count }}
              </span>
            </div>
          </div>

          <!-- Numeric Stats -->
          <div
            v-else-if="isNumericAttribute(attr) && attr.value_distribution"
            class="grid grid-cols-5 gap-4"
          >
            <div class="text-center">
              <p class="text-xs text-n-slate-10">
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.STATS.MIN') }}
              </p>
              <p class="text-lg font-medium text-n-slate-12">
                {{ attr.value_distribution.min }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xs text-n-slate-10">
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.STATS.MAX') }}
              </p>
              <p class="text-lg font-medium text-n-slate-12">
                {{ attr.value_distribution.max }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xs text-n-slate-10">
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.STATS.AVG') }}
              </p>
              <p class="text-lg font-medium text-n-slate-12">
                {{ attr.value_distribution.avg }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xs text-n-slate-10">
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.STATS.SUM') }}
              </p>
              <p class="text-lg font-medium text-n-slate-12">
                {{ attr.value_distribution.sum }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xs text-n-slate-10">
                {{ $t('CONTACT_ATTRIBUTES_REPORTS.STATS.COUNT') }}
              </p>
              <p class="text-lg font-medium text-n-slate-12">
                {{ attr.value_distribution.count }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="!attributesData.length"
          class="text-center py-8 text-n-slate-11"
        >
          {{ $t('CONTACT_ATTRIBUTES_REPORTS.NO_ATTRIBUTES') }}
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12 text-n-slate-11">
      {{ $t('CONTACT_ATTRIBUTES_REPORTS.NO_DATA') }}
    </div>
  </div>
</template>
